<aside
  class="sidebar"
  data-testid="sidebar"
  [class.sidebar--open]="nav.isOpen()"
  [class.sidebar--pinned]="nav.pinned()">
  <nav
    class="sidebar__rail"
    aria-label="Módulos principais"
    data-testid="sidebar-rail">
    <a
      class="sidebar__brand"
      data-testid="sidebar-brand"
      [routerLink]="homeRoute()"
      [attr.aria-label]="'Início - ' + companyName()"
      [pTooltip]="companyName()"
      [showDelay]="400"
      tooltipPosition="right">
      <span
        class="sidebar__brand-initial"
        aria-hidden="true">
        {{ companyInitial() }}
      </span>
    </a>

    <ul class="sidebar__rail-list">
      @for (domain of mainDomains(); track domain.id) {
        <li class="sidebar__rail-entry">
          <ng-container *ngTemplateOutlet="railItem; context: { $implicit: domain }" />
        </li>
      }
    </ul>

    <ul class="sidebar__rail-list sidebar__rail-list--footer">
      @for (domain of footerDomains(); track domain.id) {
        <li class="sidebar__rail-entry">
          <ng-container *ngTemplateOutlet="railItem; context: { $implicit: domain }" />
        </li>
      }
    </ul>
  </nav>

  <section
    id="sidebar-panel"
    class="sidebar__panel"
    data-testid="sidebar-panel"
    [attr.aria-label]="panelDomain()?.label"
    [attr.aria-hidden]="!nav.isOpen()"
    [attr.inert]="nav.isOpen() ? null : ''">
    @if (panelDomain(); as domain) {
      <div class="sidebar__panel-inner">
        <header class="sidebar__panel-header">
          <div class="sidebar__panel-bar">
            <h2
              class="sidebar__panel-title"
              data-testid="sidebar-panel-title">
              {{ domain.label }}
            </h2>

            <button
              type="button"
              class="sidebar__pin"
              data-testid="sidebar-pin"
              [class.sidebar__pin--active]="nav.pinned()"
              [attr.aria-pressed]="nav.pinned()"
              [attr.aria-label]="nav.pinned() ? 'Desafixar menu' : 'Fixar menu'"
              [pTooltip]="nav.pinned() ? 'Desafixar menu' : 'Fixar menu'"
              [showDelay]="400"
              tooltipPosition="bottom"
              (click)="nav.togglePinned()">
              <lucide-icon
                name="pin"
                [size]="14"
                [strokeWidth]="2" />
              <span>{{ nav.pinned() ? 'Fixado' : 'Fixar' }}</span>
            </button>

            <button
              type="button"
              class="sidebar__collapse"
              data-testid="sidebar-collapse"
              aria-label="Recolher menu"
              pTooltip="Recolher menu"
              [showDelay]="400"
              tooltipPosition="bottom"
              (click)="nav.collapse()">
              <lucide-icon
                name="panel-left-close"
                [size]="16"
                [strokeWidth]="1.75" />
            </button>
          </div>

          @if (showSearch()) {
            <label class="sidebar__search">
              <lucide-icon
                class="sidebar__search-icon"
                name="search"
                [size]="14"
                [strokeWidth]="1.75" />
              <input
                type="search"
                class="sidebar__search-input"
                data-testid="sidebar-search"
                autocomplete="off"
                [placeholder]="'Buscar em ' + domain.label + '…'"
                [attr.aria-label]="'Buscar em ' + domain.label"
                [value]="searchQuery()"
                (input)="searchQuery.set($any($event.target).value)"
                (keydown.enter)="openFirstResult()" />
            </label>
          }
        </header>

        <nav
          #scroller
          class="sidebar__panel-scroll"
          data-testid="sidebar-panel-scroll"
          [attr.aria-label]="'Navegação de ' + domain.label"
          (scroll)="rememberScroll($event)">
          @for (section of visibleSections(); track section.id) {
            <section class="sidebar__section">
              @if (section.label) {
                <h3 class="sidebar__section-label">{{ section.label }}</h3>
              }

              <ul class="sidebar__list">
                @for (item of section.items; track testId(item)) {
                  <li>
                    <a
                      class="sidebar__link"
                      [class.sidebar__link--active]="isDestinationActive(item)"
                      [attr.data-testid]="'sidebar-link-' + testId(item)"
                      [attr.data-tour]="panelAnchor(item.route)"
                      [attr.aria-current]="isDestinationActive(item) ? 'page' : null"
                      [routerLink]="item.route"
                      [queryParams]="item.queryParams"
                      (click)="handleDestinationClick($event)">
                      <lucide-icon
                        class="sidebar__link-icon"
                        [name]="item.icon"
                        [size]="16"
                        [strokeWidth]="1.75" />
                      <span class="sidebar__link-label">{{ item.label }}</span>
                    </a>
                  </li>
                }
              </ul>
            </section>
          } @empty {
            <p
              class="sidebar__empty"
              data-testid="sidebar-empty">
              Nada encontrado para “{{ searchQuery() }}”.
            </p>
          }
        </nav>
      </div>
    }
  </section>
</aside>

<ng-template
  #railItem
  let-domain>
  @if (domain.directRoute) {
    <a
      class="sidebar__rail-item"
      [class.sidebar__rail-item--active]="isDomainActive(domain)"
      [class.sidebar__rail-item--selected]="isDomainSelected(domain)"
      [attr.data-testid]="'sidebar-domain-' + domain.id"
      [attr.data-domain-id]="domain.id"
      [attr.data-tour]="tourAnchor(domain.id)"
      [attr.aria-label]="domain.label"
      [attr.aria-current]="isDomainActive(domain) ? 'page' : null"
      [routerLink]="domain.directRoute"
      [pTooltip]="domain.label"
      [showDelay]="400"
      tooltipPosition="right"
      (click)="handleDestinationClick($event)">
      <lucide-icon
        [name]="domain.icon"
        [size]="20"
        [strokeWidth]="1.75" />
    </a>
  } @else {
    <button
      type="button"
      class="sidebar__rail-item"
      [class.sidebar__rail-item--active]="isDomainActive(domain)"
      [class.sidebar__rail-item--selected]="isDomainSelected(domain)"
      [class.sidebar__rail-item--expanded]="isDomainExpanded(domain)"
      [attr.data-testid]="'sidebar-domain-' + domain.id"
      [attr.data-domain-id]="domain.id"
      [attr.data-tour]="tourAnchor(domain.id)"
      [attr.aria-label]="domain.label"
      [attr.aria-current]="isDomainActive(domain) ? 'true' : null"
      [attr.aria-expanded]="isDomainExpanded(domain)"
      aria-controls="sidebar-panel"
      [pTooltip]="domain.label"
      [showDelay]="400"
      tooltipPosition="right"
      (click)="selectDomain(domain)">
      <lucide-icon
        [name]="domain.icon"
        [size]="20"
        [strokeWidth]="1.75" />
    </button>
  }
</ng-template>
